import { useState } from 'react';
import axios from 'axios';
import API_BASE from '../../utils/apiBase';

const STATUS_OPTIONS = ['new', 'in_progress', 'closed', 'spam'];

export default function PlatformProspectInbox({ token }) {
  const [expanded, setExpanded] = useState(false);
  const [ready, setReady] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [changes, setChanges] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  async function loadInbox() {
    setBusy(true);
    setMessage('');
    try {
      const [status, rows] = await Promise.all([
        axios.get(`${API_BASE}/owner/platform-prospects/readiness`, { headers }),
        axios.get(`${API_BASE}/owner/platform-prospects`, { headers }),
      ]);
      setReady(status.data?.accepting_enquiries === true);
      setItems(rows.data?.prospects || []);
      setChanges({});
    } catch {
      setMessage('Could not load the CareDock enquiries inbox. No records were changed.');
    } finally {
      setBusy(false);
    }
  }

  function toggle() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    loadInbox();
  }

  function setField(id, key, value) {
    setChanges(previous => ({
      ...previous,
      [id]: { ...(previous[id] || {}), [key]: value },
    }));
  }

  async function save(id, existing) {
    const change = changes[id] || {};
    setBusy(true);
    setMessage('');
    try {
      await axios.patch(`${API_BASE}/owner/platform-prospects/${encodeURIComponent(id)}`, {
        status: change.status || existing.status,
        admin_notes: change.admin_notes ?? existing.admin_notes ?? '',
      }, { headers });
      await loadInbox();
      setMessage('Enquiry review saved privately.');
    } catch {
      setMessage('Could not save the enquiry review. Please check your session.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-xl border border-[#CBE7F6] bg-white p-4 sm:p-5"
      data-testid="caredock-platform-prospects">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-[#0D274E]">CareDock provider enquiries</h2>
          <p className="mt-1 text-sm text-slate-600">
            Platform leads only. Osabea and other tenant enquiries stay in their own workspaces.
          </p>
        </div>
        <button type="button" onClick={toggle} disabled={!token || busy}
          className="rounded-lg border border-[#CBE7F6] px-4 py-2 text-sm font-medium text-[#0D274E] disabled:opacity-50">
          {expanded ? 'Close inbox' : 'Open inbox'}
        </button>
      </div>
      {expanded && (
        <div className="mt-4 border-t border-slate-200 pt-4">
          <p className={ready ? 'text-sm text-emerald-700' : 'text-sm text-amber-800'}
            data-testid="platform-enquiry-readiness">
            {ready
              ? 'Verified CareDock platform enquiries are enabled.'
              : 'Public enquiries are not yet open. Legal and privacy release checks are still required.'}
          </p>
          {busy && <p className="mt-3 text-sm text-slate-600">Loading…</p>}
          {!busy && items.length === 0 && !message && (
            <p className="mt-4 text-sm text-slate-600">No CareDock provider enquiries recorded.</p>
          )}
          <div className="mt-4 space-y-3">
            {items.map(item => (
              <article key={item.id} className="rounded-lg border border-slate-200 p-4">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-[#0D274E]">{item.organisation}</h3>
                    <p className="text-sm text-slate-600">{item.contact_name}</p>
                    <a className="text-sm font-medium text-[#0077B5] underline"
                      href={`mailto:${item.email}`}>{item.email}</a>
                  </div>
                  <span className="text-xs text-slate-500">
                    {item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB') : 'Date unavailable'}
                  </span>
                </div>
                <p className="mt-3 whitespace-pre-wrap break-words text-sm text-slate-700">
                  {item.message}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Consent: {item.privacy_notice_version || 'Version not recorded'}
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-[160px_1fr_auto]">
                  <label className="text-xs font-semibold text-slate-700">
                    Status
                    <select aria-label={`Status for ${item.organisation}`}
                      className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2 text-sm"
                      disabled={busy}
                      value={changes[item.id]?.status || item.status}
                      onChange={e => setField(item.id, 'status', e.target.value)}>
                      {STATUS_OPTIONS.map(value => (
                        <option value={value} key={value}>{value.replaceAll('_', ' ')}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Internal note
                    <input aria-label={`Internal note for ${item.organisation}`}
                      className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
                      disabled={busy}
                      value={changes[item.id]?.admin_notes ?? item.admin_notes ?? ''}
                      onChange={e => setField(item.id, 'admin_notes', e.target.value)} />
                  </label>
                  <button type="button" onClick={() => save(item.id, item)}
                    disabled={busy} className="self-end rounded-lg bg-[#0D274E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    Save
                  </button>
                </div>
              </article>
            ))}
          </div>
          {message && <p role="status" className="mt-4 text-sm text-slate-700">{message}</p>}
        </div>
      )}
    </section>
  );
}
